import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import styled from '@/styles';
import {
  Backdrop,
  FooterRow,
  HeaderRow,
  HeaderTitle,
  IconButton,
  ModalCard,
  PrimaryAction,
  TextLink,
} from '../SnippetModalShared';

const Body = styled('div', {
  padding: '20px 24px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const FieldBlock = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const FieldLabel = styled('p', {
  fontSize: 13,
  fontWeight: 600,
  color: '#1C1C1C',
  margin: '0 0 8px',
  fontFamily: "'Manrope', sans-serif",
});

const TextInput = styled('input', {
  width: '100%',
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 14,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  '&::placeholder': { color: '#979797' },
});

const TextArea = styled('textarea', {
  width: '100%',
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '12px 14px',
  fontSize: 14,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  background: '#fff',
  outline: 'none',
  resize: 'none',
  boxSizing: 'border-box',
  lineHeight: 1.6,
  '&::placeholder': { color: '#979797' },
});

const ErrorLine = styled('p', {
  margin: 0,
  fontSize: 13,
  color: '#C0392B',
  fontFamily: "'Manrope', sans-serif",
});

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ width: 20, height: 20 }}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

type SaveSnippetModalProps = {
  isOpen: boolean;
  title: string;
  initialName?: string;
  initialBody: string;
  saving?: boolean;
  errorMessage?: string | null;
  onCancel: () => void;
  onSave: (payload: { name: string; body: string }) => void;
};

const SaveSnippetModal = ({
  isOpen,
  title,
  initialName = '',
  initialBody,
  saving = false,
  errorMessage,
  onCancel,
  onSave,
}: SaveSnippetModalProps) => {
  const [name, setName] = useState(initialName);
  const [body, setBody] = useState(initialBody);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setBody(initialBody);
    }
  }, [isOpen, initialName, initialBody]);

  if (!isOpen) return null;

  const canSave = name.trim().length > 0 && body.trim().length > 0;
  const headingTitle = title || 'Save instructions';

  const node = (
    <Backdrop
      role="dialog"
      aria-label={headingTitle}
      aria-modal
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <ModalCard size="save">
        <HeaderRow layout="between">
          <HeaderTitle size="lg">{headingTitle}</HeaderTitle>
          <IconButton
            type="button"
            pad="sm"
            onClick={onCancel}
            aria-label="Close"
          >
            <XIcon />
          </IconButton>
        </HeaderRow>

        <Body>
          <FieldBlock>
            <FieldLabel>Instructions title</FieldLabel>
            <TextInput
              type="text"
              placeholder="e.g. Hall A site directions"
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-label="Instructions title"
            />
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Instructions</FieldLabel>
            <TextArea
              rows={6}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              aria-label="Instructions"
            />
          </FieldBlock>
          {errorMessage ? <ErrorLine>{errorMessage}</ErrorLine> : null}
        </Body>

        <FooterRow>
          <TextLink type="button" onClick={onCancel} disabled={saving}>
            Cancel
          </TextLink>
          <PrimaryAction
            type="button"
            disabled={!canSave || saving}
            onClick={() => onSave({ name: name.trim(), body })}
          >
            {saving ? 'Saving…' : 'Save'}
          </PrimaryAction>
        </FooterRow>
      </ModalCard>
    </Backdrop>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
};

export default SaveSnippetModal;
